import React from "react";
import PropTypes from "prop-types";
import Slider from "@mui/material/Slider";
import { styled } from "@mui/material/styles";

// Кастомный Slider для видео с цветами как в VodSeekBar
const VideoSlider = styled(Slider)(({ theme }) => ({
  height: 6, // Увеличиваем толщину в два раза
  padding: "15px 0 0 0 !important", // Увеличиваем область клика, переопределяем медиазапросы

  "& .MuiSlider-thumb": {
    display: "none", // Убираем кружок полностью
  },
  "& .MuiSlider-track": {
    border: 0,
    borderRadius: 0, // Убираем border radius для прямых углов
    height: 8, // Увеличиваем толщину в два раза
    backgroundColor: theme.palette.primary.dark, // Воспроизведенная часть - яркая
  },
  "& .MuiSlider-rail": {
    backgroundColor: theme.palette.primary.dark, // Непроигранная часть - тоже primary
    borderRadius: 0, // Убираем border radius для прямых углов
    height: 8, // Увеличиваем толщину в два раза
    opacity: 0.3, // Делаем бледнее через opacity
  },

  // Переопределяем медиазапросы для touch устройств
  "@media (pointer: coarse)": {
    padding: "15px 0 0 0 !important",
  },
}));

const THUMB_POSITION_UPDATE_INTERVAL = 50;

function getCurrentTime(videoState) {
  if (!videoState) {
    return 0;
  }
  return videoState.currentTime || 0;
}

class ShortySeekBar extends React.Component {
  interval = null;
  lastTick = 0;
  lastIsRolling = false;

  constructor(props, context) {
    super(props, context);

    this.state = {
      isSeeking: false,
      sliderValue: getCurrentTime(props.videoState),
    };
  }

  isRolling() {
    const { videoState } = this.props;
    const { isSeeking } = this.state;

    if (!videoState) {
      return false;
    }

    return videoState.status === "playing" && !videoState.seeking && !isSeeking;
  }

  componentDidMount() {
    if (this.isRolling()) {
      this.lastIsRolling = true;
      this.startSliderValueUpdates();
    }
  }

  componentDidUpdate() {
    const { videoState } = this.props;
    const { isSeeking } = this.state;

    const currentIsRolling = this.isRolling();

    if (currentIsRolling !== this.lastIsRolling) {
      if (currentIsRolling) {
        this.startSliderValueUpdates();
      } else {
        this.stopSliderValueUpdates();
      }

      if (!isSeeking) {
        this.setState({
          sliderValue: getCurrentTime(videoState),
        });
      }

      this.lastIsRolling = currentIsRolling;
    }
  }

  componentWillUnmount() {
    this.stopSliderValueUpdates();
  }

  startSliderValueUpdates() {
    if (this.interval) {
      return;
    }

    this.lastTick = Date.now();
    this.interval = window.setInterval(
      this.updateSliderValue,
      THUMB_POSITION_UPDATE_INTERVAL
    );
  }

  stopSliderValueUpdates() {
    if (!this.interval) {
      return;
    }

    window.clearInterval(this.interval);
    this.interval = null;
  }

  updateSliderValue = () => {
    const { sliderValue } = this.state;
    const { videoState } = this.props;

    if (!videoState || !videoState.duration) return;

    const now = Date.now();
    const delta = now - this.lastTick;
    this.lastTick = now;

    const newValue = sliderValue + delta / 1000;
    const currentTime = getCurrentTime(videoState);

    // Если слайдер сильно отличается от реального времени (зацикливание), синхронизируем
    if (Math.abs(newValue - currentTime) > 1) {
      this.setState({
        sliderValue: currentTime,
      });
      return;
    }

    // Ограничиваем максимальной длительностью
    this.setState({
      sliderValue: Math.min(newValue, videoState.duration),
    });
  };

  handleChange = (_, value) => {
    const { isSeeking } = this.state;

    // При начале seeking'а останавливаем автообновления
    if (!isSeeking) {
      this.stopSliderValueUpdates();
      this.setState({
        isSeeking: true,
      });
    }

    this.setState({
      sliderValue: value,
    });
  };

  handleChangeCommitted = (_, value) => {
    const { videoState, videoActions } = this.props;

    // Применяем новую позицию
    if (videoActions && videoState && videoState.duration > 0) {
      videoActions.navigate(value);
    }

    this.setState({
      isSeeking: false,
      sliderValue: value,
    });
  };

  render() {
    const { videoState } = this.props;
    const { sliderValue } = this.state;

    if (!videoState) return null;

    const { duration = 0 } = videoState;

    if (!duration) return null;

    return (
      <VideoSlider
        value={sliderValue}
        min={0}
        max={duration}
        step={0.01}
        size="small"
        onChange={this.handleChange}
        onChangeCommitted={this.handleChangeCommitted}
      />
    );
  }
}

export default ShortySeekBar;

ShortySeekBar.propTypes = {
  videoState: PropTypes.shape({
    duration: PropTypes.number,
    currentTime: PropTypes.number,
    status: PropTypes.string,
    seeking: PropTypes.bool,
  }),
  videoActions: PropTypes.shape({
    navigate: PropTypes.func,
    play: PropTypes.func,
    pause: PropTypes.func,
  }),
};
